import { Link } from "react-router-dom";
import { Clock, Coins, Gift, Sparkles, Ticket, Users, ArrowRight, HelpCircle } from "lucide-react";

/**
 * Member FAQ — public page reflecting the Feb 2026 monetization rules:
 *   • 30-day free trial of Standard membership for every new member.
 *   • No 100-coin signup bonus.
 *   • Golden Ticket free spin restricted to Sundays only.
 *   • Tips + virtual gifts split (transparency for fans supporting creators).
 */
const SECTIONS = [
  {
    icon: Sparkles,
    title: "What do I get when I sign up?",
    body: (
      <>
        <p className="text-zinc-300 leading-relaxed">
          Every new member starts with a <strong>30-day free trial of the Standard
          membership</strong> — no card required, no auto-renew. Nothing else on the
          app is locked; the trial simply unlocks the paid perks (see below)
          without asking you for money upfront.
        </p>
        <p className="text-zinc-500 text-sm mt-3">
          Heads up: earlier accounts had a 100-coin welcome bonus. We retired
          that in February 2026 and replaced it with the free trial so every
          new member gets the full experience instead of a small one-time drop.
        </p>
      </>
    ),
  },
  {
    icon: Clock,
    title: "What happens when my 30-day trial ends?",
    body: (
      <>
        <p className="text-zinc-300 leading-relaxed">
          Your trial <strong>expires silently</strong> — we don't charge your card, we
          don't lock your account, we don't spam you. You keep browsing the app
          just like before; you simply lose the Standard-only perks until you
          choose to subscribe.
        </p>
        <p className="text-zinc-500 text-sm mt-3">
          You can see how many days are left in your trial from the countdown
          pill in the left rail (desktop) or the drawer (mobile).
        </p>
      </>
    ),
  },
  {
    icon: Coins,
    title: "How do coins work?",
    body: (
      <>
        <p className="text-zinc-300 leading-relaxed">
          Coins are the in-app currency for tipping creators and sending virtual
          gifts during live streams, in DMs, and on posts. Grab a pack from{" "}
          <Link to="/coins" className="text-rose-400 hover:underline">the Coin Store</Link>.
          Packs range from 500 → 10,000 coins.
        </p>
      </>
    ),
  },
  {
    icon: Gift,
    title: "How much of my tip actually reaches the creator?",
    body: (
      <>
        <p className="text-zinc-300 leading-relaxed">
          <strong>55% of every tip and virtual gift goes to the creator.</strong> My
          Panda X keeps 45% to cover payments, hosting, moderation, and platform
          growth. That's it — no extra processing fees.
        </p>
      </>
    ),
  },
  {
    icon: Ticket,
    title: "When can I open the Golden Ticket?",
    body: (
      <>
        <p className="text-zinc-300 leading-relaxed">
          The Golden Ticket is our weekly free-coin thank-you. It's available{" "}
          <strong>only on Sundays (UTC)</strong> — one spin per Sunday per account.
          Prizes range from a few coins up to a 5,000-coin jackpot. On days
          other than Sunday the ticket button is hidden from the UI.
        </p>
      </>
    ),
  },
  {
    icon: Users,
    title: "Is My Panda X 18+?",
    body: (
      <>
        <p className="text-zinc-300 leading-relaxed">
          Yes. You must be 18 or older to register. We use an age-gate on
          landing, and creators go through additional identity checks before
          they can receive payouts.
        </p>
      </>
    ),
  },
];

export default function FaqMembers() {
  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16" data-testid="faq-members-page">
        <Link to="/about" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-rose-500 inline-flex items-center gap-1 mb-6">
          ← Back to About
        </Link>

        <div className="mb-10">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-amber-400 border border-amber-400/30 px-3 py-1 mb-4">
            <HelpCircle size={12} /> Member FAQ
          </div>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-3">
            How My Panda X works, from the member side.
          </h1>
          <p className="text-zinc-400 leading-relaxed">
            Free trial, coins, tips, and the little Sunday surprise — everything
            you need to know as a new member.
          </p>
        </div>

        <div className="space-y-8" data-testid="faq-members-sections">
          {SECTIONS.map(({ icon: Icon, title, body }) => (
            <section key={title} className="border-l-2 border-amber-400/40 pl-5 py-1">
              <div className="flex items-center gap-2 mb-3">
                <Icon size={18} className="text-amber-400" />
                <h2 className="font-display text-xl">{title}</h2>
              </div>
              <div className="text-sm">{body}</div>
            </section>
          ))}
        </div>

        <div className="mt-14 border border-white/10 bg-white/[0.02] p-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-rose-500 mb-2">Something else?</div>
          <div className="font-display text-2xl mb-3">Reach out any time.</div>
          <p className="text-sm text-zinc-400 mb-4">
            DM <span className="text-rose-400">@panda</span> from your inbox and
            we'll respond within one business day.
          </p>
          <Link
            to="/inbox"
            className="inline-flex items-center gap-2 btn-crimson rounded-none px-5 py-3 text-sm"
            data-testid="faq-members-contact"
          >
            Open Inbox <ArrowRight size={14} />
          </Link>
        </div>

        <div className="mt-10 flex flex-wrap gap-3 text-xs">
          <Link to="/faq/creators" className="text-zinc-500 hover:text-rose-500" data-testid="faq-members-creator-link">
            Creator FAQ →
          </Link>
          <Link to="/about" className="text-zinc-500 hover:text-rose-500">
            About My Panda X →
          </Link>
        </div>
      </div>
    </div>
  );
}
